import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Checkbox,
  TextareaAutosize,
  Chip,
} from "@mui/material";
import "./ClientOperationSignOffTask.css";
import { useDispatch, useSelector } from "react-redux";
import {
  addSignOffTaskAction,
  getAllSignOffTasks,
  setAddSignOffTaskResponse,
} from "../../../../store/reducers/signOffTaskSlice";
import { getAllOperationStaffAction } from "../../../../store/reducers/assignWorkerSlice";
import CustomButton from "../../../../components/common/button/CustomButton";
import FilterTopSection from "./FilterTopSection";
import dayjs from "dayjs";
import Notification from "../../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import { signOffTaskHeading } from "./signOffAllFields";
import ManageJobView from "../../jobPrint/ManageJobView";

/* ===================== COMMON STYLES ===================== */

const compactTextFieldSx = {
  "& .MuiInputBase-root": {
    height: 26,
    fontSize: "0.8rem",
  },
  "& input": {
    padding: "4px 6px",
  },
  "& textarea": {
    padding: "4px 6px",
    fontSize: "0.8rem",
    height: "auto !important",
  },
};

/* ===================== COMPONENT ===================== */

const CrewSignOffTable = () => {
  const dispatch = useDispatch();
  const { signOffTasks, isAddSignOffTaskSuccess } = useSelector(
    (state: any) => state.signOffTask,
  );
  const { operationStaffs } = useSelector((state: any) => state.assignWorker);

  const today = dayjs().format("YYYY-MM-DD");
  const nextWeek = dayjs().add(7, "day").format("YYYY-MM-DD");
  const [originalCrews, setOriginalCrews] = useState<any[]>([]);
  const [filterFormData, setFilterFormData] = useState({
    startDate: today,
    endDate: nextWeek,
  });

  /** ✅ LOCAL EDITABLE STATE */
  const [crews, setCrews] = useState<any[]>([]);
  const [jobViewOpen, setJobViewOpen] = useState(false);
  const [jobViewType, setJobViewType] = useState("");
  const [isPrintConfigOpen, setIsPrintConfigOpen] = useState(false);
  const [selectedPrintMemberKeys, setSelectedPrintMemberKeys] = useState<
    string[]
  >([]);
  const [wageSheetData, setWageSheetData] = useState<any>(null);
  const [printOptions, setPrintOptions] = useState({
    showWage: true,
    showOvertime: true,
    showExtras: true,
    showTaskDetails: true,
  });

  const printMemberOptions = useMemo(() => {
    const optionMap = new Map<string, any>();

    crews.forEach((crew: any) => {
      (crew?.assets || []).forEach((asset: any) => {
        const memberId =
          asset?.id ??
          asset?.asset_id ??
          asset?.assetId ??
          asset?.member_id ??
          asset?.memberId ??
          asset?.staff_id ??
          asset?.staffId ??
          asset?.user_id ??
          asset?.userId ??
          asset?.employee_id ??
          asset?.employeeId;

        const normalizedName = String(asset?.name || "Unknown").trim();
        const key = memberId
          ? `id-${String(memberId).trim()}`
          : `name-${normalizedName.toLowerCase()}`;

        if (!optionMap.has(key)) {
          optionMap.set(key, {
            key,
            id: memberId ? String(memberId).trim() : "",
            name: normalizedName,
            source: asset,
          });
        }
      });
    });

    return Array.from(optionMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [crews]);

  const selectedPrintMembers = useMemo(
    () =>
      printMemberOptions.filter((option) =>
        selectedPrintMemberKeys.includes(option.key),
      ),
    [printMemberOptions, selectedPrintMemberKeys],
  );
  /* ===================== FETCH ===================== */

  useEffect(() => {
    dispatch(getAllSignOffTasks(filterFormData));
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [filterFormData, dispatch]);

  useEffect(() => {
    if (isAddSignOffTaskSuccess) {
      dispatch(getAllSignOffTasks(filterFormData));
      dispatch(setAddSignOffTaskResponse(false));
      dispatch(setNotificationAction({ message: [], severity: "error" }));
    }
  }, [isAddSignOffTaskSuccess, dispatch, filterFormData]);

  /* ===================== COPY API DATA → LOCAL STATE ===================== */

  useEffect(() => {
    if (signOffTasks?.length > 0) {
      const filtered = signOffTasks.filter((task: any) => task?.tasks !== null);

      setCrews(JSON.parse(JSON.stringify(filtered)));
      setOriginalCrews(JSON.parse(JSON.stringify(filtered)));
    } else {
      setCrews([]);
      setOriginalCrews([]);
    }
  }, [signOffTasks]);

  useEffect(() => {
    if (isPrintConfigOpen) {
      dispatch(
        getAllOperationStaffAction({
          page: 1,
          limit: 500,
          orderBy: "created_at",
          orderIn: "DESC",
          search: "",
          skill: "",
          is_deleted: "",
        }),
      );
    }
  }, [dispatch, isPrintConfigOpen]);

  useEffect(() => {
    if (printMemberOptions.length === 0) {
      setSelectedPrintMemberKeys([]);
      return;
    }

    setSelectedPrintMemberKeys((prev) => {
      const validSelection = prev.filter((key) =>
        printMemberOptions.some((option) => option.key === key),
      );

      if (validSelection.length > 0) {
        return validSelection;
      }

      return [printMemberOptions[0].key];
    });
  }, [printMemberOptions]);

  /* ===================== HANDLERS ===================== */

  const isDataChanged = JSON.stringify(crews) !== JSON.stringify(originalCrews);

  const handleCrewChange = (crewIndex: number, field: string, value: any) => {
    setCrews((prev) => {
      const next = [...prev];

      if (field === "complaint") {
        next[crewIndex] = {
          ...next[crewIndex],
          ...value,
        };
      } else {
        next[crewIndex] = {
          ...next[crewIndex],
          [field]: value,
        };
      }

      return next;
    });
  };

  const handleMemberChange = (
    crewIndex: number,
    memberIndex: number,
    field: string,
    value: any,
  ) => {
    setCrews((prev) => {
      const next = [...prev];
      const members = Array.isArray(next[crewIndex]?.assets)
        ? [...next[crewIndex].assets]
        : [];
      const existingMember = members[memberIndex] || {};

      members[memberIndex] = {
        ...existingMember,
        [field]: [
          "wage",
          "overtime",
          "review_score",
          "driving_bonus",
          "driving_speed",
          "driving_style",
        ].includes(field)
          ? value === ""
            ? null
            : Number(value)
          : value,
      };

      next[crewIndex].assets = members;
      return next;
    });
  };

  const handleTaskNoteChange = (
    crewIndex: number,
    taskIndex: number,
    value: string,
  ) => {
    setCrews((prev) => {
      const next = [...prev];
      const tasks = Array.isArray(next[crewIndex]?.tasks)
        ? [...next[crewIndex].tasks]
        : [];

      if (!tasks[taskIndex]) {
        return prev;
      }

      tasks[taskIndex] = {
        ...tasks[taskIndex],
        notes: value,
      };

      next[crewIndex] = {
        ...next[crewIndex],
        tasks,
      };

      return next;
    });
  };
  // ===================== SUBMIT HANDLER ===================== //
  const handleSubmit = () => {
    const validCrews: any[] = [];

    for (const crew of crews) {
      const start = crew.departed_time;
      const end = crew.returned_time;

      // ❌ If one filled and other empty
      if ((start && !end) || (!start && end)) {
        dispatch(
          setNotificationAction({
            message: ["Please fill both Departed and Returned time"],
            severity: "error",
          }),
        );
        return;
      }

      // ❌ If both empty → skip this crew
      if (!start && !end) {
        continue;
      }

      validCrews.push(crew);
    }

    if (validCrews.length === 0) {
      dispatch(
        setNotificationAction({
          message: ["No valid crew to submit"],
          severity: "error",
        }),
      );
      return;
    }

    console.log("FINAL PAYLOAD", validCrews);

    dispatch(addSignOffTaskAction({ crews: validCrews }));
  };

  const handleReset = () => {
    setCrews(JSON.parse(JSON.stringify(originalCrews)));
    // Hide error notification
    dispatch(
      setNotificationAction({
        message: [],
        severity: "error",
      }),
    );
  };
  /* ===================== RENDER ===================== */

  /*===================Print ============================ */
  const getWeekNumber = (dateValue: string) => {
    const date = new Date(dateValue);
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    );
    const dayNum = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
    return Math.ceil(
      (((utcDate as any) - (yearStart as any)) / 86400000 + 1) / 7,
    );
  };

  const toNumber = (value: any) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getHoursBetween = (departed?: string, returned?: string) => {
    if (!departed || !returned) return 0;

    const start = dayjs(`2000-01-01 ${departed}`);
    let end = dayjs(`2000-01-01 ${returned}`);

    if (!start.isValid() || !end.isValid()) return 0;
    if (end.isBefore(start)) {
      end = end.add(1, "day");
    }

    return Number((end.diff(start, "minute") / 60).toFixed(2));
  };

  const buildWageSheetForMember = (selectedPrintMember: any) => {
    const selectedOperationStaff = (operationStaffs || []).find(
      (staff: any) => String(staff?.id) === String(selectedPrintMember?.id),
    );

    const rows = crews
      .flatMap((crew: any) => {
        const matchedAsset = (crew?.assets || []).find((asset: any) => {
          const memberId =
            asset?.id ??
            asset?.asset_id ??
            asset?.assetId ??
            asset?.member_id ??
            asset?.memberId ??
            asset?.staff_id ??
            asset?.staffId ??
            asset?.user_id ??
            asset?.userId ??
            asset?.employee_id ??
            asset?.employeeId;

          if (selectedPrintMember?.id) {
            return String(memberId || "") === String(selectedPrintMember.id);
          }

          return (
            String(asset?.name || "")
              .trim()
              .toLowerCase() ===
            String(selectedPrintMember?.name || "")
              .trim()
              .toLowerCase()
          );
        });

        if (!matchedAsset) return [];

        const wage = toNumber(matchedAsset?.wage);
        const overtime = toNumber(matchedAsset?.overtime);
        const extra = toNumber(matchedAsset?.review);
        const drive = toNumber(matchedAsset?.driving_bonus);
        const hours = getHoursBetween(crew?.departed_time, crew?.returned_time);

        const taskDetails = (crew?.tasks || [])
          .map((task: any) => {
            const postcodes = [
              ...(task?.collection_addresses || []),
              ...(task?.delivery_addresses || []),
            ]
              .map((address: any) => address?.postcode)
              .filter(Boolean)
              .join(" -> ");

            const detailSegments = [
              task?.name,
              task?.customer_name ? `(${task.customer_name})` : "",
              postcodes,
              task?.notes,
            ].filter(Boolean);

            return detailSegments.join(" ");
          })
          .filter(Boolean)
          .join(" | ");

        const customers = (crew?.tasks || [])
          .map((task: any) => task?.customer_name || task?.name)
          .filter(Boolean)
          .join(", ");

        return [
          {
            rawDate: crew?.date,
            day: crew?.date ? dayjs(crew.date).format("ddd D MMM") : "-",
            crewName: crew?.name || "-",
            customer: customers || "-",
            notes: matchedAsset?.notes || "",
            start: crew?.departed_time || "",
            finish: crew?.returned_time || "",
            hours,
            wage,
            overtime,
            extra,
            drive,
            total: wage + overtime + extra,
            taskDetails,
          },
        ];
      })
      .sort(
        (a: any, b: any) =>
          dayjs(a.rawDate).valueOf() - dayjs(b.rawDate).valueOf(),
      );

    const totals = rows.reduce(
      (acc: any, row: any) => ({
        totalHours: acc.totalHours + toNumber(row.hours),
        totalWage: acc.totalWage + toNumber(row.wage),
        totalOvertime: acc.totalOvertime + toNumber(row.overtime),
        totalExtra: acc.totalExtra + toNumber(row.extra),
        totalDrive: acc.totalDrive + toNumber(row.drive),
      }),
      {
        totalHours: 0,
        totalWage: 0,
        totalOvertime: 0,
        totalExtra: 0,
        totalDrive: 0,
      },
    );

    const daysWorked = rows.length;
    const outOfDays = Math.max(
      1,
      dayjs(filterFormData.endDate).diff(
        dayjs(filterFormData.startDate),
        "day",
      ) + 1,
    );
    const averageHours =
      daysWorked > 0 ? Number((totals.totalHours / daysWorked).toFixed(2)) : 0;
    const grandTotal =
      totals.totalWage +
      totals.totalOvertime +
      totals.totalExtra +
      totals.totalDrive;

    const week = getWeekNumber(filterFormData.startDate);
    const year = dayjs(filterFormData.startDate).format("YYYY");
    const rawStaffDigits = String(selectedPrintMember?.id || "")
      .replace(/\D/g, "")
      .slice(-4);
    const suffix = rawStaffDigits
      ? rawStaffDigits.padStart(4, "0")
      : dayjs().format("HHmm");
    const documentNumber = `${year}/${String(week).padStart(2, "0")}-${suffix}`;

    return {
      printDate: dayjs().format("DD-MM-YY"),
      documentNumber,
      selectedDateRange: {
        startDate: filterFormData.startDate,
        endDate: filterFormData.endDate,
      },
      staff: {
        name: selectedOperationStaff?.aka_name
          ? `${selectedOperationStaff?.name || selectedPrintMember?.name} [${selectedOperationStaff?.aka_name}]`
          : selectedOperationStaff?.name || selectedPrintMember?.name || "",
        address:
          selectedOperationStaff?.address ||
          selectedPrintMember?.source?.address ||
          "",
        niNumber:
          selectedOperationStaff?.ni_number ||
          selectedPrintMember?.source?.ni_number ||
          "",
      },
      options: printOptions,
      rows,
      summary: {
        daysWorked,
        outOfDays,
        averageHours,
        ...totals,
        grandTotal,
      },
    };
  };

  const buildWageSheetData = () => {
    if (selectedPrintMembers.length === 0) return null;

    const sheets = selectedPrintMembers
      .map((member) => buildWageSheetForMember(member))
      .filter((sheet) => Array.isArray(sheet?.rows) && sheet.rows.length > 0);

    return {
      options: printOptions,
      sheets,
    };
  };

  const handlePrintWages = () => {
    if (printMemberOptions.length === 0) {
      dispatch(
        setNotificationAction({
          message: ["No staff found for selected date range"],
          severity: "error",
        }),
      );
      return;
    }

    setIsPrintConfigOpen(true);
  };

  const handleConfirmPrint = () => {
    if (selectedPrintMemberKeys.length === 0) {
      dispatch(
        setNotificationAction({
          message: ["Please select at least one staff member"],
          severity: "error",
        }),
      );
      return;
    }

    const payload = buildWageSheetData();

    if (
      !payload ||
      !Array.isArray(payload?.sheets) ||
      payload.sheets.length === 0
    ) {
      dispatch(
        setNotificationAction({
          message: ["No wage rows available for selected staff"],
          severity: "error",
        }),
      );
      return;
    }

    setWageSheetData(payload);
    setIsPrintConfigOpen(false);
    setJobViewType("WAGES");
    setJobViewOpen(true);
  };

  const handleClose = () => {
    setJobViewOpen(false);
    setJobViewType("");
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            background: "#fff",
            padding: "10px 0",
          }}
        >
          <FilterTopSection
            filteredData={filterFormData}
            setFilteredData={setFilterFormData}
          />
          <Box sx={{ display: "flex" }}>
            {crews?.length > 0 && (
              <CustomButton
                title="Print"
                onClick={handlePrintWages}
                // disabled={!isDataChanged}
                variant="outlined"
                sx={{ marginRight: "8px" }}
              />
            )}
            <CustomButton
              title="Reset"
              onClick={handleReset}
              disabled={!isDataChanged}
              variant="outlined"
              sx={{ marginRight: "8px" }}
            />
            {crews?.length > 0 && (
              <CustomButton
                title="Submit Sign Off"
                onClick={handleSubmit}
                style={{
                  backgroundColor: "#02906f",
                  color: "#fff",
                  fontWeight: 600,
                  padding: "10px 20px",
                }}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ padding: "0px" }}>
          <Notification />
          <Box className="operation-signoff-container">
            <div
              className="table-wrapper"
              style={{
                maxHeight: "calc(100vh - 180px)",
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "80px",
                  marginLeft: "230px",
                  fontWeight: 600,
                  fontSize: "13px",
                  color: "#0f8a6c",
                }}
              >
                Complaint
              </Box>

              <table
                className="crew-table"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                }}
              >
                <thead>
                  <tr>
                    {signOffTaskHeading.map((heading, index) => (
                      <th
                        key={index}
                        colSpan={heading.colspan || 1}
                        style={{
                          width: heading.width,
                          position: "sticky",
                          top: 0,
                          zIndex: 20,
                          border: "1px solid #ddd",
                        }}
                      >
                        {heading.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {crews?.length > 0 ? (
                    crews.map((crew, crewIndex) => {
                      const drivers = (crew.assets || []).filter(
                        (a: any) => a?.type?.toUpperCase() === "DRIVER",
                      );

                      const porters = (crew.assets || []).filter(
                        (a: any) => a?.type?.toUpperCase() === "PORTER",
                      );

                      const allMembers = [...drivers, ...porters];

                      const vehicles = crew.vehicles || [];

                      const vehicleGroups =
                        vehicles.length > 0
                          ? vehicles.map((vehicle: any) => ({
                              vehicle,
                              members: [],
                            }))
                          : [
                              {
                                vehicle: null,
                                members: allMembers,
                              },
                            ];

                      // Round Robin assignment
                      allMembers.forEach((member: any, index: number) => {
                        vehicleGroups[
                          index % vehicleGroups.length
                        ].members.push(member);
                      });

                      const displayAssets = vehicleGroups.flatMap(
                        (group: any) =>
                          group.members.map(
                            (member: any, memberIndex: number) => ({
                              ...member,
                              originalIndex: crew.assets.findIndex(
                                (a: any) => a.id === member.id,
                              ),
                              vehicleName: group.vehicle?.name || "-",
                              showVehicle: memberIndex === 0,
                              vehicleRowSpan: group.members.length,
                            }),
                          ),
                      );

                      const assets = displayAssets.length
                        ? displayAssets
                        : [null];

                      const tasks = crew.tasks?.length ? crew.tasks : [null];

                      const rowSpan = Math.max(assets.length, tasks.length);
                      const isFutureDate = dayjs(crew.date).isAfter(
                        dayjs(),
                        "day",
                      );

                      return Array.from({ length: rowSpan }).map(
                        (_, rowIndex) => {
                          const asset = assets[rowIndex] || null;
                          const task = tasks[rowIndex] || null;
                          const taskPostcodes = [
                            ...(task?.collection_addresses || []),
                            ...(task?.delivery_addresses || []),
                          ]
                            .map((addr: any) => addr?.postcode)
                            .filter((postcode: any) => Boolean(postcode));
                          const originalCrew = originalCrews.find(
                            (c: any) => c?.id === crew?.id,
                          );
                          const isComplaintLocked = Boolean(
                            originalCrew?.is_crew_complaints ||
                            originalCrew?.is_customer_complaints,
                          );
                          const foremanId =
                            crew?.foreman_id ??
                            crew?.foremanId ??
                            crew?.foreman?.id;
                          const memberIds = [
                            asset?.id,
                            asset?.asset_id,
                            asset?.assetId,
                            asset?.member_id,
                            asset?.memberId,
                            asset?.staff_id,
                            asset?.staffId,
                            asset?.user_id,
                            asset?.userId,
                            asset?.employee_id,
                            asset?.employeeId,
                          ]
                            .filter(Boolean)
                            .map((id: any) => String(id).trim());
                          const isTeamLeader =
                            Boolean(foremanId) &&
                            memberIds.includes(String(foremanId).trim());

                          return (
                            <tr
                              key={`${crewIndex}-${rowIndex}`}
                              className={
                                isFutureDate ? "future-row-disabled" : ""
                              }
                            >
                              {/* DAY + CREW */}
                              {rowIndex === 0 && (
                                <>
                                  <td rowSpan={rowSpan}>
                                    <strong>
                                      {dayjs(crew.date).format("ddd DD MMM YY")}
                                    </strong>
                                  </td>

                                  <td rowSpan={rowSpan} colSpan={2}>
                                    <strong>{crew.name}</strong>

                                    <div className="time-wrapper">
                                      <span className="table-title">
                                        Departed
                                      </span>
                                      <TextField
                                        type="time"
                                        size="small"
                                        value={crew.departed_time || ""}
                                        sx={compactTextFieldSx}
                                        onChange={(e) =>
                                          handleCrewChange(
                                            crewIndex,
                                            "departed_time",
                                            e.target.value,
                                          )
                                        }
                                        InputProps={{
                                          style: {
                                            width: "100%",
                                          },
                                        }}
                                      />
                                    </div>

                                    <div className="time-wrapper">
                                      <span className="table-title">
                                        Returned
                                      </span>
                                      <TextField
                                        type="time"
                                        size="small"
                                        value={crew.returned_time || ""}
                                        sx={compactTextFieldSx}
                                        onChange={(e) =>
                                          handleCrewChange(
                                            crewIndex,
                                            "returned_time",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                  </td>

                                  <td rowSpan={rowSpan} align="center">
                                    <Checkbox
                                      size="small"
                                      checked={crew.is_crew_complaints}
                                      disabled={isComplaintLocked}
                                      onChange={(e) =>
                                        handleCrewChange(
                                          crewIndex,
                                          "complaint",
                                          {
                                            is_crew_complaints:
                                              e.target.checked,
                                            is_customer_complaints: false,
                                          },
                                        )
                                      }
                                    />
                                  </td>

                                  <td rowSpan={rowSpan} align="center">
                                    <Checkbox
                                      size="small"
                                      checked={crew.is_customer_complaints}
                                      disabled={isComplaintLocked}
                                      onChange={(e) =>
                                        handleCrewChange(
                                          crewIndex,
                                          "complaint",
                                          {
                                            is_customer_complaints:
                                              e.target.checked,
                                            is_crew_complaints: false,
                                          },
                                        )
                                      }
                                    />
                                  </td>
                                </>
                              )}

                              {/* MEMBER */}
                              <td className="table-title">
                                {asset?.name || "-"}
                              </td>
                              <td className="table-title">
                                {asset?.type ? (
                                  <Chip
                                    sx={{ color: "#fff", fontWeight: 500 }}
                                    label={asset?.type || "N/A"}
                                    size="small"
                                    color="success"
                                    variant="filled"
                                  />
                                ) : (
                                  "--"
                                )}
                              </td>

                              {/* VEHICLE */}

                              {asset?.showVehicle ? (
                                <td
                                  className="table-title"
                                  rowSpan={asset.vehicleRowSpan}
                                >
                                  {asset.vehicleName || "-"}
                                </td>
                              ) : !asset ? (
                                <td className="table-title">-</td>
                              ) : null}

                              {/* LEADER */}
                              <td align="center">
                                <Checkbox
                                  size="small"
                                  checked={isTeamLeader}
                                  disabled
                                />
                              </td>

                              {/* WAGE / OT */}
                              <td>
                                {asset ? (
                                  <TextField
                                    size="small"
                                    value={asset?.wage ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "wage",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset ? (
                                  <TextField
                                    size="small"
                                    value={asset?.overtime ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "overtime",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset ? (
                                  <TextField
                                    size="small"
                                    value={asset?.review ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "review",
                                        e.target.value,
                                      )
                                    }
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset ? (
                                  <TextField
                                    size="small"
                                    value={asset?.review_score ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "review_score",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset?.type?.toLowerCase() === "driver" ? (
                                  <TextField
                                    size="small"
                                    value={asset?.driving_bonus ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "driving_bonus",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset?.type?.toLowerCase() === "driver" ? (
                                  <TextField
                                    size="small"
                                    value={asset?.driving_speed ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "driving_speed",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset?.type?.toLowerCase() === "driver" ? (
                                  <TextField
                                    size="small"
                                    value={asset?.driving_style ?? ""}
                                    sx={compactTextFieldSx}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "driving_style",
                                        e.target.value,
                                      )
                                    }
                                    inputProps={{
                                      maxLength: 4,
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td>
                                {asset ? (
                                  <TextareaAutosize
                                    minRows={4}
                                    maxRows={12}
                                    value={asset?.notes || ""}
                                    onChange={(e) =>
                                      handleMemberChange(
                                        crewIndex,
                                        asset.originalIndex,
                                        "notes",
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      fontSize: "14px",
                                      padding: "6px 8px",
                                      lineHeight: "1.4",
                                      resize: "none",
                                      borderRadius: "4px",
                                      border: "1px solid #c4c4c4",
                                      fontFamily: "inherit",
                                      boxSizing: "border-box",
                                    }}
                                  />
                                ) : (
                                  "-"
                                )}
                              </td>

                              <td className="table-title">
                                {task?.name || "-"}
                                {task?.customer_name && (
                                  <>
                                    <br />({task?.customer_name})
                                    {taskPostcodes?.length > 0 && (
                                      <>
                                        <br />
                                        {taskPostcodes.map(
                                          (postcode: string, idx: number) => (
                                            <React.Fragment
                                              key={`${task?.id || "task"}-${idx}`}
                                            >
                                              {idx > 0 && <br />}
                                              {postcode}
                                            </React.Fragment>
                                          ),
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </td>
                              <td>
                                {task ? (
                                  <>
                                    <TextareaAutosize
                                      minRows={2}
                                      maxRows={12}
                                      value={task?.notes || ""}
                                      placeholder="Enter task notes"
                                      onChange={(e) =>
                                        handleTaskNoteChange(
                                          crewIndex,
                                          rowIndex,
                                          e.target.value,
                                        )
                                      }
                                      style={{
                                        width: "100%",
                                        fontSize: "14px",
                                        padding: "6px 8px",
                                        lineHeight: "1.4",
                                        resize: "none",
                                        borderRadius: "4px",
                                        border: "1px solid #c4c4c4",
                                        fontFamily: "inherit",
                                        boxSizing: "border-box",
                                      }}
                                    />
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>
                                {task ? (
                                  <>
                                    <TextareaAutosize
                                      minRows={2}
                                      maxRows={12}
                                      disabled={true}
                                      value={task?.transport_note || ""}
                                      placeholder="Enter transport notes"
                                      onChange={(e) =>
                                        handleTaskNoteChange(
                                          crewIndex,
                                          rowIndex,
                                          e.target.value,
                                        )
                                      }
                                      style={{
                                        width: "100%",
                                        fontSize: "13px",
                                        padding: "6px 8px",
                                        lineHeight: "1.4",
                                        resize: "vertical",
                                        borderRadius: "4px",
                                        border: "1px solid #c4c4c4",
                                        fontFamily: "inherit",
                                        boxSizing: "border-box",
                                      }}
                                    />
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          );
                        },
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={signOffTaskHeading?.length} align="center">
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "120px",
                            border: "1px dashed #ccc",
                            borderRadius: "12px",
                            backgroundColor: "#fafafa",
                            textAlign: "center",
                            fontSize: "16px",
                            p: 3,
                          }}
                        >
                          No crews available
                        </Box>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Box>
        </Grid>
      </Grid>

      {jobViewOpen && (
        <ManageJobView
          open={jobViewOpen}
          onHide={handleClose}
          title="Wage Sign Off"
          jobViewType={jobViewType}
          wageSheetData={wageSheetData}
        />
      )}

      <Dialog
        open={isPrintConfigOpen}
        onClose={() => setIsPrintConfigOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Wage Sheet Print Options</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, mb: 2 }}>
            <Box sx={{ mb: 0.5, fontWeight: 600, fontSize: "0.9rem" }}>
              Staff/Crew Member
            </Box>
            <Select
              fullWidth
              size="small"
              multiple
              value={selectedPrintMemberKeys}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const value = e.target.value;
                setSelectedPrintMemberKeys(
                  typeof value === "string" ? value.split(",") : value,
                );
              }}
              renderValue={(selected) =>
                printMemberOptions
                  .filter((option) => selected.includes(option.key))
                  .map((option) => option.name)
                  .join(", ")
              }
            >
              {printMemberOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  <Checkbox
                    checked={selectedPrintMemberKeys.includes(option.key)}
                  />
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.showWage}
                  onChange={(e) =>
                    setPrintOptions((prev) => ({
                      ...prev,
                      showWage: e.target.checked,
                    }))
                  }
                />
              }
              label="Show Wage"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.showOvertime}
                  onChange={(e) =>
                    setPrintOptions((prev) => ({
                      ...prev,
                      showOvertime: e.target.checked,
                    }))
                  }
                />
              }
              label="Show Overtime"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.showExtras}
                  onChange={(e) =>
                    setPrintOptions((prev) => ({
                      ...prev,
                      showExtras: e.target.checked,
                    }))
                  }
                />
              }
              label="Show Extras"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={printOptions.showTaskDetails}
                  onChange={(e) =>
                    setPrintOptions((prev) => ({
                      ...prev,
                      showTaskDetails: e.target.checked,
                    }))
                  }
                />
              }
              label="Show Task Details"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <CustomButton
            title="Cancel"
            onClick={() => setIsPrintConfigOpen(false)}
            variant="outlined"
          />
          <CustomButton
            title="Preview & Print"
            onClick={handleConfirmPrint}
            style={{
              backgroundColor: "#02906f",
              color: "#fff",
              fontWeight: 600,
              padding: "8px 16px",
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CrewSignOffTable;
